import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faHeartPulse, faPlay, faStethoscope } from "@fortawesome/free-solid-svg-icons";
import AppShell, { PageHeader } from "../components/shell/app-shell";
import Icon3D from "../components/ui/icon-3d";
import Segmented from "../components/ui/segmented";
import SidePanel from "../components/ui/side-panel";
import StatusPill from "../components/ui/status-pill";
import SkeletonList from "../components/ui/skeleton-list";
import { LatencyBadge } from "../components/ui/latency";
import { PanelTitle } from "../components/ui/detail";
import { TONES } from "../components/ui/tones";
import TimeAgo from "../components/time-ago";
import useQueryState from "../hooks/use-query-state";
import useAutoRefresh from "../hooks/use-auto-refresh";
import { usePlatform } from "../store/use-platform";
import { fetchHealth, fetchHealthHistory, runHealthCheck } from "../utils/api";
import { CHECK_LOOK, PLATFORM_LOOK, platformLabel } from "../utils/status";
import { formatDateTime, formatDuration } from "../utils/format-time";
import { CheckKind, HealthCheck, HealthChecksOverview, PlatformHealthCheck } from "../types/health-check";
import { ParseErrors } from "../utils/ParseErrors";

const KIND_NOTE: Record<CheckKind, string> = {
  pulse: "Every hour: is the server up and does its database answer?",
  full: "Daily at 12:00: every service the platform uses (email, SMS, storage, payments…).",
};

const SERVICE_TONE = { up: "emerald", degraded: "amber", down: "rose" } as const;

function HistoryPanel({ row, kind, onClose }: { row: PlatformHealthCheck | null; kind: CheckKind; onClose: () => void }) {
  const [checks, setChecks] = useState<HealthCheck[] | null>(null);

  useEffect(() => {
    setChecks(null);
    if (!row) return;
    fetchHealthHistory({ platform: row.platform, kind, limit: 20 })
      .then((result) => setChecks(result.data))
      .catch((error) => toast.error(ParseErrors(error)));
  }, [row, kind]);

  const look = row ? PLATFORM_LOOK[row.platform] : null;

  return (
    <SidePanel
      open={!!row}
      onClose={onClose}
      icon={look ? <Icon3D icon={look.icon} tone={look.tone} size="lg" /> : null}
      title={row ? `${platformLabel(row.platform)} · ${kind === "pulse" ? "pulse" : "health check"} history` : ""}
      subtitle={KIND_NOTE[kind]}
    >
      {!checks ? (
        <SkeletonList rows={5} />
      ) : !checks.length ? (
        <p className="text-[13px] text-light-gray">No checks yet.</p>
      ) : (
        <ul className="space-y-2">
          {checks.map((check) => {
            const checkLook = CHECK_LOOK[check.status];
            return (
              <li key={check.id} className="glass p-4">
                <div className="flex items-center justify-between gap-3">
                  <StatusPill tone={checkLook.tone} live={checkLook.live}>{checkLook.label}</StatusPill>
                  <span className="font-[Gilroy-Medium] text-[12px] text-label-muted">{formatDateTime(check.createdAt)} · {check.trigger}</span>
                </div>
                {check.error ? <p className="mt-2 font-[Gilroy-Medium] text-[13px] text-rose-600 dark:text-rose-300">{check.error}</p> : null}
                <ul className="mt-3 space-y-1.5">
                  {check.services.map((service) => (
                    <li key={service.id} className="flex items-center justify-between gap-3 font-[Gilroy-Medium] text-[13px]">
                      <span className="flex items-center gap-2 text-heading-color"><span className={clsx("h-2 w-2 rounded-full", TONES[SERVICE_TONE[service.status]].dot)} />{service.name}</span>
                      <LatencyBadge ms={service.latencyMs} />
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </SidePanel>
  );
}

export default function HealthPage() {
  const { platform } = usePlatform();
  const [kind, setKind] = useQueryState<string>("kind", "pulse");
  const [overview, setOverview] = useState<HealthChecksOverview | null>(null);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState<PlatformHealthCheck | null>(null);
  const checkKind = kind as CheckKind;

  const load = useCallback(async () => {
    try {
      setOverview(await fetchHealth(checkKind));
    } catch (error) {
      toast.error(ParseErrors(error));
    }
  }, [checkKind]);

  useEffect(() => {
    setOverview(null);
    void load();
  }, [load]);
  useAutoRefresh(load, 60000);

  const runNow = async () => {
    setRunning(true);
    try {
      await runHealthCheck(checkKind);
      toast.success(checkKind === "pulse" ? "Pulse finished" : "Health check finished");
      await load();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setRunning(false);
    }
  };

  const rows = (overview?.platforms ?? []).filter((row) => platform === "all" || row.platform === platform);

  return (
    <AppShell>
      <PageHeader
        title="Health"
        subtitle={KIND_NOTE[checkKind]}
        actions={
          <button
            type="button"
            onClick={runNow}
            disabled={running || overview?.isRunning}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 px-4 font-[Gilroy-Medium] text-[13px] text-white shadow-[0_10px_24px_-10px_rgba(99,102,241,0.8)] transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faPlay} className={clsx("text-[11px]", running && "animate-spin")} />
            {running ? "Checking every platform…" : `Run ${checkKind === "pulse" ? "pulse" : "full check"} now`}
          </button>
        }
      />

      <div className="mb-6">
        <Segmented label="Check type" value={kind} onChange={setKind} options={[{ label: "Pulse · hourly", value: "pulse" }, { label: "Full check · daily", value: "full" }]} />
      </div>

      {!overview ? (
        <SkeletonList rows={3} height="h-48" />
      ) : (
        <div className="stagger grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => {
            const look = PLATFORM_LOOK[row.platform];
            const status = row.latest?.status ?? "not-configured";
            const checkLook = CHECK_LOOK[status];
            return (
              <button key={row.platform} type="button" onClick={() => setSelected(row)} className="glass glass-hover flex flex-col gap-4 p-5 text-left">
                <div className="flex items-center gap-3">
                  <Icon3D icon={look.icon} tone={look.tone} size="md" flat />
                  <div className="min-w-0 flex-1">
                    <p className="font-[Gilroy-Bold] text-[16px] text-heading-color">{look.label}</p>
                    <p className="font-[Gilroy-Medium] text-[12px] text-label-muted">
                      {row.latest ? <>Checked <TimeAgo value={row.latest.createdAt} /> · {formatDuration(row.latest.durationMs)}</> : row.configured ? "No checks yet" : "Status URL not set"}
                    </p>
                  </div>
                  <StatusPill tone={checkLook.tone} live={checkLook.live}>{checkLook.label}</StatusPill>
                </div>

                {row.latest?.error ? <p className="rounded-xl bg-rose-500/5 px-3 py-2 font-[Gilroy-Medium] text-[13px] text-rose-600 dark:text-rose-300">{row.latest.error}</p> : null}

                <ul className="space-y-2">
                  {(row.latest?.services ?? []).map((service) => (
                    <li key={service.id} className="flex items-center justify-between gap-3 font-[Gilroy-Medium] text-[13px]">
                      <span className="flex items-center gap-2 text-heading-color">
                        <Icon3D icon={service.key === "database" ? faStethoscope : service.key === "server" ? faBolt : faHeartPulse} tone={SERVICE_TONE[service.status]} size="sm" flat className="!h-6 !w-6 !rounded-lg !text-[10px]" />
                        {service.name}
                      </span>
                      <LatencyBadge ms={service.latencyMs} />
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <PanelTitle>Last {row.recent.length} checks</PanelTitle>
                  <div className="mt-2 flex h-4 gap-[3px]">
                    {row.recent.length ? (
                      row.recent.map((tick) => <span key={tick.createdAt} title={`${CHECK_LOOK[tick.status].label} · ${formatDateTime(tick.createdAt)}`} className={clsx("flex-1 rounded-[3px]", TONES[CHECK_LOOK[tick.status].tone].dot)} />)
                    ) : (
                      <span className="flex-1 rounded-[3px] bg-overlay-mid" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <HistoryPanel row={selected} kind={checkKind} onClose={() => setSelected(null)} />
    </AppShell>
  );
}
